import { useState } from "react";
import { Icon } from "./icons";
import { useContent } from "./content";
import { FS_STAGES, FS_HOURS, FS_STAGE_CHIPS, FS_GEAR, FS_CREW, FS_LOGI, STAGE_C, fsPos, posIn, hhmm, FS_CREW_T0, FS_CREW_T1, FS_LOGI_T0, FS_LOGI_T1, FS_CREW_TICKS, FS_LOGI_TICKS } from "./data";

// A small horizontal inset so consecutive blocks/shifts/trips don't touch —
// gives the "air between acts" without lying about the proportions.
const bar = (p: { left: number; width: number }) => ({
  left: `calc(${p.left}% + 3px)`,
  width: `calc(${Math.max(p.width, 0)}% - 6px)`,
});

function StatusDot({ st }: { st: "ok" | "warn" | "tight" }) {
  if (st === "ok") return <span className="fs-dot" style={{ background: "var(--ok)" }} />;
  return <Icon name="triangle" size={12} style={{ color: st === "tight" ? "var(--warn)" : "var(--bad)" }} />;
}

function TimelineView() {
  return (
    <div className="fs-tl">
      <div className="fs-tl__search"><Icon name="search" size={13} />Find artist on grid</div>
      <div className="fs-tl__day">FRI · JUL 24</div>
      <div className="fs-tl__grid">
        <div className="fs-tl__ruler">
          <span className="fs-tl__rlabel">STAGE</span>
          <div className="fs-tl__ticks">{FS_HOURS.map((h) => <span key={h} className="fs-tl__tick">{String(h % 24).padStart(2, "0")}:00</span>)}</div>
        </div>
        {FS_STAGES.map((s) => (
          <div key={s.k} className="fs-tl__row">
            <div className="fs-tl__stage">
              <span className="fs-tl__handle" />
              <span className="fs-tl__sdot" style={{ background: STAGE_C[s.k] }} />
              <div className="fs-tl__sbody">
                <div className="fs-tl__sname">{s.n}</div>
                <div className="fs-tl__smeta"><span>{s.sets} sets</span><span className="fs-tl__step">− <b>2F</b> +</span></div>
              </div>
            </div>
            <div className="fs-tl__track">
              {FS_HOURS.map((h, i) => i === 0 ? null : <span key={h} className="fs-tl__gl" style={{ left: (i / (FS_HOURS.length - 1)) * 100 + "%" }} />)}
              {s.blocks.map((b, i) => {
                const p = fsPos(b[1], b[2]);
                return (
                  <div key={i} className={"fs-blk" + (b[3] !== "ok" ? " fs-blk--flag" : "")} style={{ ...bar(p), ["--c" as string]: STAGE_C[s.k] }}>
                    <div className="fs-blk__top"><span className="fs-blk__name">{b[0]}</span><StatusDot st={b[3]} /></div>
                    <div className="fs-blk__time">{hhmm(b[1])}–{hhmm(b[2])}</div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ConsoleView() {
  const t = useContent().arrange;
  return (
    <div className="fs-cn">
      <div className="fs-cn__bar">
        <span className="fs-cn__lbl">STAGES · 5</span>
        <div className="fs-cn__chips">
          {FS_STAGE_CHIPS.map((c, i) => <span key={c} className={"fs-chip" + (i === 3 ? " fs-chip--on" : "")}><span className="fs-chip__d" />{c}{i === 3 && <em> viewing</em>}<span className="fs-chip__f">2F</span></span>)}
        </div>
      </div>
      <div className="fs-cn__status">
        <span className="fs-cn__fit"><span className="fs-dot" style={{ background: "var(--ok)" }} />{t.consoleStatus.fit}</span>
        <span className="fs-cn__req">{t.consoleStatus.requested} · 4 CDJ · 2 mixer · 2 TT · on fomet 8</span>
        <span className="fs-cn__save">{t.consoleStatus.savedDay}</span>
      </div>
      <div className="fs-cn__tools">
        {["+ CDJ", "+ Mixer", "+ Turntable", "+ Power", "Arrange"].map((x) => <span key={x} className="fs-cn__tool">{x}</span>)}
        <span className="fs-cn__spacer" /><span className="fs-cn__dim">Fomet 2 · W 200 · D 100</span>
      </div>
      <div className="fs-cn__plot">
        <div className="fs-cn__aud">AUDIENCE</div>
        <div className="fs-cn__fomets">
          <div className="fs-cn__fomet">
            <div className="fs-cn__ftag">FOMET 1</div>
            <div className="fs-cn__gear">
              {FS_GEAR.map((g, i) => (
                <div key={i} className={"fs-gear" + (g[1] === "DJM-V10" ? " fs-gear--mix" : "")}>
                  <span className="fs-gear__n">{g[0]}</span><span className="fs-gear__scr" /><span className="fs-gear__m">{g[1]}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="fs-cn__fomet">
            <div className="fs-cn__ftag">FOMET 2</div>
            <div className="fs-cn__gear fs-cn__gear--sparse">
              <div className="fs-gear fs-gear--mix"><span className="fs-gear__n">Mixer 2</span><span className="fs-gear__scr" /><span className="fs-gear__m">Xone:96</span></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CrewView() {
  const c = useContent().arrange.crew;
  return (
    <div className="fs-cr">
      <div className="fs-cr__head"><span className="fs-cr__title">{c.title}</span><span className="fs-cr__peak">{c.peak} <b>22</b> · {c.needed} <b>10</b> / stage</span></div>
      <div className="fs-cr__ticks">{FS_CREW_TICKS.map((h) => <span key={h}>{String(h % 24).padStart(2, "0")}:00</span>)}</div>
      {FS_CREW.map((row, i) => (
        <div key={i} className="fs-cr__row">
          <div className="fs-cr__role"><span>{c.roles[i]}</span><span className="fs-cr__n">×{row.n}</span></div>
          <div className="fs-cr__track">
            {row.shifts.map((sh, j) => { const p = posIn(sh[0], sh[1], FS_CREW_T0, FS_CREW_T1); return <div key={j} className="fs-cr__shift" style={bar(p)}>{hhmm(sh[0])}</div>; })}
          </div>
        </div>
      ))}
    </div>
  );
}

function LogisticsView() {
  const l = useContent().arrange.logistics;
  return (
    <div className="fs-lg">
      <div className="fs-lg__cols">
        <div className="fs-lg__gantt">
          <div className="fs-lg__head"><span>{l.heading}</span><span className="fs-lg__flag"><Icon name="triangle" size={11} style={{ color: "var(--warn)" }} />{l.clash}</span></div>
          <div className="fs-lg__ticks">{FS_LOGI_TICKS.map((h) => <span key={h}>{String(h % 24).padStart(2, "0")}:00</span>)}</div>
          {FS_LOGI.map((row, i) => (
            <div key={i} className="fs-lg__row">
              <div className="fs-lg__unit">{l.units[i]}</div>
              <div className="fs-lg__track">
                {row.trips.map((tp, j) => { const p = posIn(tp[0], tp[1], FS_LOGI_T0, FS_LOGI_T1); return <div key={j} className={"fs-lg__trip" + (i === 0 && j === 1 ? " fs-lg__trip--clash" : "")} style={bar(p)} />; })}
              </div>
            </div>
          ))}
        </div>
        <div className="fs-lg__side">
          {l.cards.map((cd, i) => <div key={i} className="fs-lg__card"><div className="fs-lg__k">{cd.k}</div><div className="fs-lg__v">{cd.v}</div></div>)}
        </div>
      </div>
    </div>
  );
}

export function FestivalSurface() {
  const t = useContent().arrange;
  const [tab, setTab] = useState<"timeline" | "console" | "crew" | "logistics">("timeline");
  const tabs = [
    { id: "timeline", label: t.tabs.timeline, icon: "grid" },
    { id: "console", label: t.tabs.console, icon: "layers" },
    { id: "crew", label: t.tabs.crew, icon: "users" },
    { id: "logistics", label: t.tabs.logistics, icon: "pin" },
  ] as const;
  return (
    <div className="frame fs">
      <div className="frame__bar fs__bar" role="tablist" aria-label="Festival surface">
        <div className="frame__dots"><i /><i /><i /></div>
        <div className="fs__tabs">
          {tabs.map((x) => (
            <button key={x.id} role="tab" aria-selected={tab === x.id} className={"fs__tab" + (tab === x.id ? " fs__tab--on" : "")} onClick={() => setTab(x.id)}>
              <Icon name={x.icon} size={13} />{x.label}
            </button>
          ))}
        </div>
        <span className="frame__tag">MERIDIANO 2026</span>
      </div>
      <div className="fs__body">
        {tab === "timeline" && <TimelineView />}
        {tab === "console" && <ConsoleView />}
        {tab === "crew" && <CrewView />}
        {tab === "logistics" && <LogisticsView />}
      </div>
    </div>
  );
}
